import { type JSX } from "react";
import { NavLink } from "react-router-dom";

import type { AppDescriptorDTO } from "../api/types.js";
import { AppIcon } from "./AppIcon.js";
import { useModalFocus } from "./useModalFocus.js";

/**
 * Alle Bereiche als Raster, wie der App-Bildschirm eines Telefons.
 *
 * Die schmale Leiste zeigt nur Symbole; hier stehen die Namen dazu. Das ist der Ort,
 * an dem man nachschaut, statt zu raten.
 */
export function AppGrid({
  apps,
  onClose,
}: {
  apps: AppDescriptorDTO[];
  onClose: () => void;
}): JSX.Element {
  const panel = useModalFocus(onClose);

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <button
        type="button"
        aria-label="Apps schließen"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      <div
        role="dialog"
        ref={panel}
        tabIndex={-1}
        aria-modal="true"
        aria-label="Alle Apps"
        className="relative mt-auto max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-line bg-surface-raised p-5 pb-[calc(theme(spacing.5)+env(safe-area-inset-bottom))] shadow-2xl sm:m-auto sm:max-w-md sm:rounded-2xl sm:border sm:pb-5"
      >
        <h2 className="mb-4 text-center text-sm font-semibold text-ink">Apps</h2>
        <button type="button" onClick={onClose} className="mb-3 rounded-lg p-2 text-sm text-ink-muted">
          Schließen
        </button>

        <ul className="grid grid-cols-4 gap-2">
          {apps.map((app) => (
            <li key={app.id}>
              <NavLink
                to={app.route}
                end={app.route === "/"}
                onClick={onClose}
                className={({ isActive }) =>
                  [
                    "flex flex-col items-center gap-2 rounded-xl px-1 py-3",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand",
                    isActive ? "bg-brand/10" : "hover:bg-surface-sunken",
                  ].join(" ")
                }
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-sunken text-brand">
                  <AppIcon name={app.icon} className="h-6 w-6" />
                </span>
                <span className="line-clamp-2 w-full break-words text-center text-[11px] leading-tight text-ink">
                  {app.name}
                </span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
